import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { t } from "../i18n";
import { AlertIcon, AlertTriangleIcon, LogOutIcon, SendIcon, Spinner, TrashIcon } from "./Icons";
import { useModalPresence, useLastOpenValue, overlayMotionProps, dialogMotionProps } from "../utils/modalPresence";
import { useEscapeLayer, ESCAPE_PRIORITY_MODAL } from "../utils/escapeLayers";
import { settleConfirm, useConfirmState } from "../utils/confirmDialog";

const ICONS = { trash: TrashIcon, send: SendIcon, alert: AlertIcon, logout: LogOutIcon };

// Окно подтверждений (см. utils/confirmDialog.js). Ставится один раз — в App.
const ConfirmDialogHost = () => {
  const state = useConfirmState();
  const open = Boolean(state && !state.closed);
  const presence = useModalPresence(open);
  const shown = useLastOpenValue(state, open);
  // Пока действие идёт, окно не закрывается ни Escape, ни щелчком мимо.
  const cancel = () => { if (!shown?.busy) settleConfirm(false); };
  useEscapeLayer(open, cancel, ESCAPE_PRIORITY_MODAL + 5);
  // Фокус — на само окно, а не на кнопку: Enter не должен случайно удалить, а
  // Tab сразу ведёт к кнопкам.
  const cardRef = useRef(null);
  const openId = open ? state.id : 0;
  useEffect(() => {
    if (openId) cardRef.current?.focus({ preventScroll: true });
  }, [openId]);

  if (!presence.mounted || !shown || typeof document === "undefined") return null;
  const danger = shown.variant === "danger";
  const Icon = ICONS[shown.icon] || (danger ? AlertTriangleIcon : AlertIcon);
  const note = shown.note === false ? "" : (shown.note || (danger ? t("Действие необратимо") : ""));
  const titleId = `kl-confirm-title-${shown.id}`;

  return createPortal(
    <div className="kl-confirm-overlay" {...overlayMotionProps(presence.state)} onMouseDown={(event) => { if (event.target === event.currentTarget) cancel(); }}>
      <div
        ref={cardRef}
        tabIndex={-1}
        className="kl-confirm"
        data-variant={shown.variant}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        {...dialogMotionProps(presence.state)}
      >
        <div className="kl-confirm-head">
          <span className="kl-confirm-icon" aria-hidden="true"><Icon size={20} /></span>
          <div className="kl-confirm-text">
            <div className="kl-confirm-title" id={titleId}>{shown.title || t("Подтверждение действия")}</div>
            {shown.message ? <div className="kl-confirm-message">{shown.message}</div> : null}
            {note ? <div className="kl-confirm-note">{note}</div> : null}
          </div>
        </div>
        <div className="kl-confirm-actions">
          <button type="button" className="kl-confirm-btn" onClick={cancel} disabled={shown.busy}>
            {shown.cancelText || t("Отмена")}
          </button>
          <button
            type="button"
            className="kl-confirm-btn is-primary"
            data-variant={shown.variant}
            onClick={() => settleConfirm(true)}
            disabled={shown.busy}
          >
            {shown.busy ? <Spinner size={15} /> : null}
            {shown.confirmText || t("Подтвердить")}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ConfirmDialogHost;
